import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Trees, Mountain, Waves, ChevronLeft, Footprints } from 'lucide-react';
import { getPokemonLocalIconUrl } from '../../utils/pokemonIconUtils';
import { getPokemonDisplayParts } from '../../utils/pokemonDisplayName';
import { isUncatchableRarePokemon } from '../../utils/pokemonRarity';
import mapBg from '../../assets/map/map.png';
import pokeballImg from '../../assets/pokeball.png';
import mapBackImg from '../../assets/map/map-back.png';
import mapViewportImg from '../../assets/map/map-viewport.png';
import mapDesignImg from '../../assets/map/map-design.png';
import mapNameImg from '../../assets/map/map-name.png';
import searchGoImg from '../../assets/map/search-go.png';
import arrowTopImg from '../../assets/map/arrow_top.png';
import arrowBottomImg from '../../assets/map/arrow_bottom.png';
import arrowLeftImg from '../../assets/map/arrow_left.png';
import arrowRightImg from '../../assets/map/arrow_right.png';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';

const DEFAULT_VIEWPORT = { x: 0, y: 0, w: 100, h: 100 };
const SCREEN_RATIO = 1545 / 765;
const TOWN_LABEL_CENTER_Y_OFFSET = 0.075;
// mapBg(map.png) 실측 픽셀 비율(2539×2535 ≈ 1.0016). map.png를 다른 비율의 이미지로
// 교체하면 이 값도 같이 갱신해야 한다.
const MAP_IMAGE_ASPECT = 2539 / 2535;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// mapBg 원본 이미지는 거의 정사각형(2539×2535)이라 SCREEN_RATIO(909/655, 기기 화면 비율)와
// 전혀 다르다. lockedH가 이 둘을 같다고 착각하고 h를 계산하면, 배경(backgroundSize: X% auto,
// 이미지 자체 비율로 세로가 정해짐)과 핀 좌표(viewport.h 기준으로 세로 % 계산)가 서로 다른
// 세로 스케일을 쓰게 되어 화면 중심에서 멀어진 핀일수록(패닝할수록) 라벨이 실제 그림 위치에서
// 점점 벗어나 보인다. imageAspect(이미지 자체의 가로/세로 비율)를 반드시 반영해야 한다.
function lockedH(width, containerRect, imageAspect) {
  const containerAspect = containerRect && containerRect.width && containerRect.height
    ? containerRect.width / containerRect.height
    : SCREEN_RATIO;
  return width * imageAspect / containerAspect;
}

function getCenteredViewport(town, viewport) {
  const width = Number(viewport.w) || DEFAULT_VIEWPORT.w;
  const height = Number(viewport.h) || DEFAULT_VIEWPORT.h;

  return {
    ...viewport,
    x: clamp((Number(town.x) || 50) - width / 2, 0, 100 - width),
    y: clamp((Number(town.y) || 50) - height * (0.5 + TOWN_LABEL_CENTER_Y_OFFSET), 0, 100 - height),
  };
}

function getViewportBgStyle(vp) {
  const { x, y, w, h } = vp;
  const bgSizeW = w >= 100 ? '100%' : `${(10000 / w).toFixed(4)}%`;
  const bgPosX  = w >= 100 ? '0%' : `${(x * 100 / (100 - w)).toFixed(4)}%`;
  const bgPosY  = h >= 100 ? '0%' : `${(y * 100 / (100 - h)).toFixed(4)}%`;
  return {
    backgroundImage: `url("${mapBg}")`,
    backgroundSize: `${bgSizeW} auto`,
    backgroundPosition: `${bgPosX} ${bgPosY}`,
  };
}

function toViewportCoords(px, py, vp) {
  return {
    x: ((px - vp.x) / vp.w) * 100,
    y: ((py - vp.y) / vp.h) * 100,
  };
}

// 사철록 여름/가을/겨울 모습처럼 일부 폼은 number가 숫자가 아니라
// "pokemon-form-10068"처럼 문자열 ID다. Number()로 못 바꾸는 값을 전부 null로
// 버리면 이런 폼들은 gamePokedexNumbers/unlockedNumbers Set에 절대 들어갈 수
// 없어서 실제로 잡았어도 포획 표시가 안 된다. 숫자로 못 바꾸는 값은 문자열
// 그대로 키로 써서(양쪽 다 이 함수를 거치므로 비교는 여전히 일관됨) 살려둔다.
const toDexNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : String(value);
};


// map-back.png 안쪽 투명 영역 (2053×908 기준, PowerShell로 측정)
// x=0~1545, y=85~850 → 1545×765 px
const SCREEN = {
  left:   '0%',
  top:    '9.36%',
  right:  '24.74%',
  bottom: '6.39%',
};

export default function MapView({
  regions,
  onRegionClick,
  gamePokedex = [],
  allPokemonMaster = [],
  pokedexData = {},
  caughtPokemon = [],
  dailyWalks = 0,
  maxDailyWalks = 0,
}) {
  const [selectedTownId, setSelectedTownId] = useState(null);
  const [openTownRegionsId, setOpenTownRegionsId] = useState(null);
  const [screenView, setScreenView] = useState('map'); // 'map' | 'detail'
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const [mapViewportLoaded, setMapViewportLoaded] = useState(false);
  const lastDefaultTownIdRef = useRef(null);
  const lastCenteredDefaultTownIdRef = useRef(null);
  const [tvPhase, setTvPhase] = useState(0); // 0: 검은화면, 1: 애니메이션, 2: 종료
  const [visitedTownIds, setVisitedTownIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('mapVisitedTownIds') || '[]'));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    get(ref(database, 'gameData/config/mapViewport')).then(snap => {
      if (!snap.exists()) return;
      const saved = snap.val();
      const screenRect = screenRef.current?.getBoundingClientRect() ?? null;
      const height = lockedH(Number(saved.w) || DEFAULT_VIEWPORT.w, screenRect, MAP_IMAGE_ASPECT);
      setViewport({ ...saved, h: height });
    }).finally(() => {
      setMapViewportLoaded(true);
    });
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setTvPhase(1), 60);
    const t2 = setTimeout(() => setTvPhase(2), 550); // 확장 시작 타이밍에 콘텐츠 팝인
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const towns = useMemo(() => {
    const townMap = new Map();
    regions.forEach(r => {
      if (r.isTownMeta && r.groupId) {
        townMap.set(r.groupId, {
          groupId: r.groupId,
          groupName: r.groupName || r.groupId,
          isDefaultTown: r.isDefaultTown || false,
          groupVisible: r.groupVisible,
          townOrder: Number.isFinite(Number(r.townOrder)) ? Number(r.townOrder) : townMap.size,
          color: r.color || '#10b981',
          x: r.x ?? 50,
          y: r.y ?? 50,
        });
      }
    });
    regions.forEach(r => {
      if (!r.isTownMeta && r.groupId && r.groupName && !townMap.has(r.groupId)) {
        townMap.set(r.groupId, {
          groupId: r.groupId,
          groupName: r.groupName,
          isDefaultTown: r.isDefaultTown || false,
          groupVisible: r.groupVisible,
          townOrder: Number.isFinite(Number(r.townOrder)) ? Number(r.townOrder) : townMap.size,
          color: r.color || '#10b981',
          x: r.x ?? 50,
          y: r.y ?? 50,
        });
      }
    });
    const orderedTowns = Array.from(townMap.values()).sort((a, b) => a.townOrder - b.townOrder);
    const defaultTownIndex = orderedTowns.findIndex(town => town.isDefaultTown);
    const boundaryIndex = defaultTownIndex >= 0 ? defaultTownIndex : 0;

    return orderedTowns.filter((town, index) => town.groupVisible !== false || index < boundaryIndex);
  }, [regions]);

  const navigableTowns = useMemo(() => (
    towns.filter(town => town.groupVisible !== false)
  ), [towns]);

  const arrowTowns = towns;

  const defaultTownId = useMemo(() => (
    (navigableTowns.find(town => town.isDefaultTown) || navigableTowns[0])?.groupId || null
  ), [navigableTowns]);

  useEffect(() => {
    if (!defaultTownId) return;
    const previousDefaultTownId = lastDefaultTownIdRef.current;
    lastDefaultTownIdRef.current = defaultTownId;

    setSelectedTownId(prev => {
      if (
        prev &&
        previousDefaultTownId === defaultTownId &&
        navigableTowns.some(town => town.groupId === prev)
      ) {
        return prev;
      }

      return defaultTownId;
    });
  }, [defaultTownId, navigableTowns]);

  const selectedTown = towns.find(t => t.groupId === selectedTownId) || null;

  // Pokédex helpers
  const caughtNumbers = useMemo(() => new Set(
    caughtPokemon.flatMap(p => p ? [p.number, p.originalNumber] : []).map(toDexNumber).filter(Boolean)
  ), [caughtPokemon]);

  const unlockedNumbers = useMemo(() => new Set([
    ...Object.keys(pokedexData || {}).map(toDexNumber).filter(Boolean),
    ...caughtNumbers,
  ]), [pokedexData, caughtNumbers]);

  const gamePokedexNumbers = useMemo(() => new Set(
    gamePokedex.map(p => toDexNumber(p.number)).filter(Boolean)
  ), [gamePokedex]);

  const isPokemonUnlocked = (pokemon = {}) => {
    const number = toDexNumber(pokemon.number);
    const originalNumber = toDexNumber(pokemon.originalNumber);
    if (number && unlockedNumbers.has(number)) return true;
    if (originalNumber && unlockedNumbers.has(originalNumber)) return true;
    return allPokemonMaster.some(form => (
      form.originalNumber === pokemon.number &&
      gamePokedexNumbers.has(toDexNumber(form.number)) &&
      unlockedNumbers.has(toDexNumber(form.number))
    ));
  };


  const handleSelectArea = (area) => {
    const places = Array.isArray(area.places) ? area.places.filter(p => p?.name && p.visible !== false) : [];
    setSelectedArea(area);
    setSelectedPlace(places.length > 0 ? places[0] : null);
    setScreenView('detail');
  };

  const handleExplore = () => {
    if (!onRegionClick) return;
    const region = selectedArea;
    if (selectedPlace) {
      onRegionClick({
        ...region, ...selectedPlace,
        id: `${region.id}__place__${selectedPlace.id}`,
        baseRegionId: region.id,
        regionId: region.id,
        regionName: region.name,
        regionMaxLevel: region.maxLevel,
        lootConfig: selectedPlace.lootConfig || region.lootConfig,
        placeId: selectedPlace.id,
        placeName: selectedPlace.name,
        name: `${region.name} - ${selectedPlace.name}`,
      });
    } else {
      onRegionClick(region);
    }
  };

  const animateTownToCenter = useCallback((town) => {
    const vp = viewportRef.current;
    const screenRect = screenRef.current?.getBoundingClientRect();
    let centerXFrac = 0.5;
    let centerYFrac = 0.5;
    if (screenRect) {
      const upRect    = arrowBtnRefs.up.current?.getBoundingClientRect();
      const downRect  = arrowBtnRefs.down.current?.getBoundingClientRect();
      const leftRect  = arrowBtnRefs.left.current?.getBoundingClientRect();
      const rightRect = arrowBtnRefs.right.current?.getBoundingClientRect();
      const areaTop    = upRect    ? upRect.bottom    - screenRect.top  : 0;
      const areaBottom = downRect  ? downRect.top     - screenRect.top  : screenRect.height;
      const areaLeft   = leftRect  ? leftRect.right   - screenRect.left : 0;
      const areaRight  = rightRect ? rightRect.left   - screenRect.left : screenRect.width;
      centerXFrac = (areaLeft + areaRight) / 2 / screenRect.width;
      centerYFrac = ((areaTop + areaBottom) / 2 + 45) / screenRect.height;
    }
    const targetX = Math.max(0, Math.min(100 - vp.w, town.x - vp.w * centerXFrac));
    const targetY = Math.max(0, Math.min(100 - vp.h, town.y - vp.h * centerYFrac));
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    const startX = vp.x, startY = vp.y;
    const startTime = performance.now();
    const duration = 220;
    const ease = t => 1 - Math.pow(1 - t, 3);
    const frame = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const e = ease(t);
      setViewport(v => ({ ...v, x: startX + (targetX - startX) * e, y: startY + (targetY - startY) * e }));
      if (t < 1) animFrameRef.current = requestAnimationFrame(frame);
    };
    animFrameRef.current = requestAnimationFrame(frame);
  }, []);

  useLayoutEffect(() => {
    if (!mapViewportLoaded || tvPhase < 2 || !defaultTownId) return;
    if (lastCenteredDefaultTownIdRef.current === defaultTownId) return;

    const defaultTown = navigableTowns.find(town => town.groupId === defaultTownId);
    if (!defaultTown) return;

    lastCenteredDefaultTownIdRef.current = defaultTownId;
    setViewport(currentViewport => getCenteredViewport(defaultTown, currentViewport));
  }, [defaultTownId, mapViewportLoaded, navigableTowns, tvPhase]);

  const handlePinClick = (townId) => {
    setSelectedTownId(townId);
    setOpenTownRegionsId(townId);
    setVisitedTownIds(prev => {
      const next = new Set(prev);
      next.add(townId);
      try {
        localStorage.setItem('mapVisitedTownIds', JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
    setScreenView('map');
    const town = towns.find(t => t.groupId === townId);
    if (town) animateTownToCenter(town);
  };

  const getDisplayPokemon = (area, place) => {
    const ids = place && Array.isArray(place.pokemons) && place.pokemons.length > 0
      ? place.pokemons : (area.pokemons || []);
    const pool = area.allowNationalPokedex ? allPokemonMaster : gamePokedex;
    const matched = pool.filter(p => ids.includes(p.id) || ids.includes(p.number));

    // 장소에서 특정 폼(예: 사철록 여름/겨울 모습)을 명시적으로 골라도, 그 폼이 "게임
    // 도감"에는 원종(봄의 모습)만 등록되어 있고 폼 자체는 별도 등록이 안 돼 있으면 위
    // 필터에서 조용히 빠져서 항상 원종만 보이는 것처럼 나온다 — 실제 조우 로직
    // (useRegionExplore.js)에 이미 있는 것과 같은 보정을 미리보기에도 적용한다.
    const matchedIds = new Set();
    matched.forEach(p => { matchedIds.add(p.id); matchedIds.add(p.number); });
    const missingIds = ids.filter(id => !matchedIds.has(id));
    const fallback = missingIds.length > 0
      ? allPokemonMaster.filter(p => missingIds.includes(p.id) || missingIds.includes(p.number))
      : [];

    // 전설/환상/패러독스 포켓몬은 지역 설정에 포함돼 있어도 등장 포켓몬 미리보기에는 노출하지 않는다.
    return [...matched, ...fallback].filter(p => !isUncatchableRarePokemon(p));
  };


  // 첫 번째 지역이 오른쪽에 오도록
  // 5글자 초과 시 줄바꿈 (마지막 줄이 1글자가 되면 한 글자 앞에서 나눔)
  const wrapPillText = (text) => {
    const MAX = 5;
    if (text.length <= MAX) return [text];
    const lines = [];
    let str = text;
    while (str.length > MAX) {
      const leftover = str.slice(MAX).length;
      const cut = leftover === 1 ? MAX - 1 : MAX;
      lines.push(str.slice(0, cut));
      str = str.slice(cut);
    }
    lines.push(str);
    return lines;
  };

  const regionPillPositions = [
    null,                                                                                                   // 0: 오른쪽 — 래퍼로 처리
    { top: -56,    left: 0, right: 0, marginLeft: 'auto', marginRight: 'auto', width: 'fit-content' },   // 1: 위
    { bottom: -56, left: 0, right: 0, marginLeft: 'auto', marginRight: 'auto', width: 'fit-content' }, // 2: 아래
    null,                                                                                                   // 3: 왼쪽 — 래퍼로 처리
  ];

  const screenRef = useRef(null);
  const arrowBtnRefs = { up: useRef(null), down: useRef(null), left: useRef(null), right: useRef(null) };
  const viewportRef = useRef(viewport);
  useEffect(() => { viewportRef.current = viewport; }, [viewport]);
  const animFrameRef = useRef(null);
  const dragRef = useRef(null); // { startX, startY, startVpX, startVpY }
  const [isDragging, setIsDragging] = useState(false);

  const handleMapMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (screenView !== 'map') return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startVpX: viewportRef.current.x,
      startVpY: viewportRef.current.y,
    };
    setIsDragging(false);
  }, []);

  const handleMapMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (!isDragging && Math.hypot(dx, dy) < 4) return;
    setIsDragging(true);
    const rect = screenRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mapDx = (dx / rect.width)  * viewportRef.current.w;
    const mapDy = (dy / rect.height) * viewportRef.current.h;
    const newX = Math.max(0, Math.min(100 - viewportRef.current.w, dragRef.current.startVpX - mapDx));
    const newY = Math.max(0, Math.min(100 - viewportRef.current.h, dragRef.current.startVpY - mapDy));
    setViewport(v => ({ ...v, x: newX, y: newY }));
  }, [isDragging]);

  const handleMapMouseUp = useCallback(() => {
    dragRef.current = null;
    setTimeout(() => setIsDragging(false), 0);
  }, []);
  const clickAudioRef = useRef(null);
  useEffect(() => {
    const basePath = window.location.pathname.includes('/poke-commu-system') ? '/poke-commu-system' : '';
    const audio = new Audio(`${basePath}/sound/A-button.mp3`);
    audio.preload = 'auto';
    audio.volume = 0.5;
    clickAudioRef.current = audio;
  }, []);

  const playClickSound = () => {
    const audio = clickAudioRef.current;
    if (!audio) return;
    audio.currentTime = 0.2;
    audio.play().catch(() => {});
  };

  const navigateToDirection = (dir) => {
    if (arrowTowns.length === 0) return;
    const selectedTown = arrowTowns.find(t => t.groupId === selectedTownId);
    const cx = selectedTown ? selectedTown.x : viewport.x + viewport.w / 2;
    const cy = selectedTown ? selectedTown.y : viewport.y + viewport.h / 2;

    // 방향별로 해당 방향에 있는 마을 필터 후 가장 가까운 것
    const candidates = arrowTowns.filter(t => {
      const dx = t.x - cx;
      const dy = t.y - cy;
      if (dir === 'right') return dx > 1;
      if (dir === 'left')  return dx < -1;
      if (dir === 'down')  return dy > 1;
      if (dir === 'up')    return dy < -1;
      return false;
    });

    if (candidates.length === 0) return;

    // 해당 방향 축 기준 가장 가까운 마을
    const nearest = candidates.reduce((best, t) => {
      const dist = Math.hypot(t.x - cx, t.y - cy);
      const bestDist = Math.hypot(best.x - cx, best.y - cy);
      return dist < bestDist ? t : best;
    });

    animateTownToCenter(nearest);
    setSelectedTownId(nearest.groupId);
    setOpenTownRegionsId(null);
    playClickSound();
  };

  const arrowImgs = { up: arrowTopImg, down: arrowBottomImg, left: arrowLeftImg, right: arrowRightImg };

  const arrowHasTowns = useMemo(() => {
    const selectedTown = arrowTowns.find(t => t.groupId === selectedTownId);
    const cx = selectedTown ? selectedTown.x : viewport.x + viewport.w / 2;
    const cy = selectedTown ? selectedTown.y : viewport.y + viewport.h / 2;
    const has = (dir) => arrowTowns.some(t => {
      const dx = t.x - cx, dy = t.y - cy;
      if (dir === 'right') return dx > 1;
      if (dir === 'left')  return dx < -1;
      if (dir === 'down')  return dy > 1;
      if (dir === 'up')    return dy < -1;
      return false;
    });
    return { up: has('up'), down: has('down'), left: has('left'), right: has('right') };
  }, [arrowTowns, viewport, selectedTownId]);

  const ArrowBtn = ({ dir }) => (
    <button
      ref={arrowBtnRefs[dir]}
      type="button"
      onClick={(e) => { e.stopPropagation(); navigateToDirection(dir); }}
      style={{
        position: 'absolute',
        ...(dir === 'up'    && { top: 10,    left: '50%', transform: 'translateX(-50%)' }),
        ...(dir === 'down'  && { bottom: 10, left: '50%', transform: 'translateX(-50%)' }),
        ...(dir === 'left'  && { left: 10,   top: '50%',  transform: 'translateY(-50%)' }),
        ...(dir === 'right' && { right: 10,  top: '50%',  transform: 'translateY(-50%)' }),
        zIndex: dir === 'up' ? 70 : 20,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        opacity: arrowHasTowns[dir] ? 1 : 0.3,
        transition: 'opacity 0.2s',
      }}
    >
      <img src={arrowImgs[dir]} alt={dir} style={{ display: 'block', width: 36, height: 36, imageRendering: 'auto', filter: 'drop-shadow(0 3px 3px rgba(0,0,0,0.35))' }} />
    </button>
  );

  /* ── 스크린 내 상세 뷰 ── */

  const DeviceMobileDetailPanel = () => {
    if (!selectedArea) return null;
    const places = Array.isArray(selectedArea.places) ? selectedArea.places.filter(p => p?.name && p.visible !== false) : [];
    const accentColor = selectedArea.color || selectedTown?.color || '#089a4f';
    const areaPokemon = getDisplayPokemon(selectedArea, selectedPlace);
    const activeLevel = selectedPlace
      ? { min: selectedPlace.minLevel ?? selectedArea.minLevel ?? 1, max: selectedPlace.maxLevel ?? selectedArea.maxLevel ?? 20 }
      : { min: selectedArea.minLevel ?? 1, max: selectedArea.maxLevel ?? 20 };
    const activeRate = selectedPlace ? (selectedPlace.encounterRate ?? selectedArea.encounterRate) : selectedArea.encounterRate;

    return (
      <>
        {/* 배경 오버레이 */}
        <div className="absolute inset-0" style={{ background: 'rgba(0, 0, 0, 0.42)', pointerEvents: 'none' }} />

        {/* 본문 — zIndex 없음 → 내부 버튼이 마스크(66) 위로 올라갈 수 있음 */}
        <div className="absolute inset-0" style={{ overflow: 'hidden' }}>
          <div style={{ height: '100%', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', padding: '18px 14px 50px' }}>
            {/* 뒤로가기 */}
            <button
              onClick={() => setScreenView('map')}
              style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12, background: 'rgba(255,255,255,0.92)', border: 'none', borderRadius: 10, padding: '6px 10px 6px 6px', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.15)', zIndex: 67, position: 'relative', flexShrink: 0 }}
            >
              <ChevronLeft size={18} color="#373a33" />
              <span style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 12, fontWeight: 700, color: '#373a33' }}>뒤로</span>
            </button>

            {/* 좌: 등장 포켓몬 / 우: 지역·장소 */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flex: 1, minHeight: 0 }}>
              {/* 왼쪽 — 등장 포켓몬 (이 영역만 스크롤) */}
              <div style={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto', paddingRight: 2 }}>
                <div style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.75)', marginBottom: 10 }}>
                  등장 포켓몬 <span style={{ color: 'rgba(180,230,100,0.9)' }}>{areaPokemon.length}</span>
                </div>

                {areaPokemon.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                    등록된 포켓몬이 없습니다
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4 }}>
                    {areaPokemon.map(p => {
                      const caught = caughtNumbers.has(Number(p.number));
                      const known = caught || isPokemonUnlocked(p);
                      const iconUrl = known ? getPokemonLocalIconUrl({ ...p, nameEn: p.nameEn || p.name || 'UNKNOWN' }) : null;
                      // "이름 (폼 설명)" 형태는 뒤 괄호를 떼고, 리전 폼(알로라/가라르/히스이/팔데아)만
                      // 이름 아래에 작게 리전 이름을 따로 표시한다.
                      const { name: displayName, formLabel } = getPokemonDisplayParts(p);
                      const isRegionalLabel = !!p.regionalForm;
                      return (
                        <div key={p.id || p.number} style={{
                          position: 'relative',
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          border: `1.5px solid ${caught ? 'rgba(74,154,8,0.30)' : 'rgba(255,255,255,0.5)'}`,
                          borderRadius: 9,
                          padding: '6px 2px 5px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 3,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                          overflow: 'hidden',
                        }}>
                          {caught && <img src={pokeballImg} alt="" style={{ position: 'absolute', width: '48%', top: '50%', left: '50%', transform: 'translate(-50%, -53%)', opacity: 0.9, zIndex: 0 }} />}
                          <div style={{ width: 27, height: 27, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                            {iconUrl ? (
                              <div style={{ width: 27, height: 27, backgroundImage: `url(${iconUrl})`, backgroundSize: '54px 27px', backgroundPosition: 'left center', backgroundRepeat: 'no-repeat', imageRendering: 'pixelated' }} />
                            ) : (
                              <span style={{ fontSize: 14, color: 'rgba(0,0,0,0.15)' }}>?</span>
                            )}
                          </div>
                          <div style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 10, fontWeight: 700, color: known ? '#102e1f' : '#bbb', textAlign: 'center', lineHeight: 1.3, position: 'relative', zIndex: 1 }}>
                            {known ? displayName : '???'}
                          </div>
                          {known && isRegionalLabel && formLabel && (
                            <div style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 7, fontWeight: 600, color: 'rgba(26,46,16,0.55)', textAlign: 'center', lineHeight: 1, marginTop: -2, position: 'relative', zIndex: 1 }}>
                              {formLabel}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 오른쪽 — 지역 이름 + 장소 선택 */}
              <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: 14, borderRadius: 16, padding: '16px 18px', background: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}>
                  <div style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 20, fontWeight: 700, color: '#373a33', lineHeight: 1.2, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>
                    {selectedArea.name}
                  </div>
                  <div style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 13, fontWeight: 700, color: '#65c493', marginTop: 2, marginBottom: 10 }}>
                    {selectedTown?.groupName}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 11, fontWeight: 700, color: '#089a4f', background: 'rgba(74,154,8,0.13)', borderRadius: 20, padding: '2px 8px' }}>
                      Lv.{activeLevel.min}-{activeLevel.max}
                    </span>
                    {activeRate !== undefined && (
                      <span style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 11, fontWeight: 700, color: '#407a5c', background: 'rgba(255,255,255,0.85)', borderRadius: 20, padding: '2px 8px', border: '1px solid rgba(120,180,60,0.22)' }}>
                        {activeRate < 1 ? Math.round(activeRate * 100) : activeRate}%
                      </span>
                    )}
                  </div>
                </div>

                {places.length > 0 && (
                  <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {places.map(place => {
                      const active = selectedPlace?.id === place.id;
                      return (
                        <button
                          key={place.id}
                          onClick={() => setSelectedPlace(place)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '11px 14px',
                            background: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.70)',
                            border: `1.5px solid ${active ? accentColor : 'rgba(255,255,255,0.4)'}`,
                            borderRadius: 12,
                            cursor: 'pointer',
                            textAlign: 'left',
                            boxShadow: active ? '0 2px 12px rgba(74,154,8,0.25)' : 'none',
                          }}
                        >
                          <span style={{ color: active ? accentColor : '#407a5c', display: 'flex', alignItems: 'center' }}>
                            {place.isCave ? <Mountain size={18} /> : place.isWaterside ? <Waves size={18} /> : <Trees size={18} />}
                          </span>
                          <span style={{ flex: 1, fontFamily: 'GmarketSans, sans-serif', fontSize: 15, fontWeight: 500, color: '#373a33', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', position: 'relative', top: 3 }}>{place.name}</span>
                          <span style={{ fontFamily: 'GmarketSans, sans-serif', fontSize: 11, color: '#407a5c' }}>
                            Lv.{place.minLevel ?? selectedArea.minLevel ?? 1}-{place.maxLevel ?? selectedArea.maxLevel ?? 20}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <button onClick={handleExplore} style={{
                  width: '100%',
                  padding: 0,
                  borderRadius: 18,
                  background: 'transparent',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.35))',
                }}>
                  <img src={searchGoImg} alt={selectedPlace ? `${selectedPlace.name} 탐험하기` : '탐험하기'} style={{ width: 150, maxWidth: '58%', display: 'block' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      className="map-view-root w-full flex items-start justify-center"
      style={{ marginTop: -58, paddingBottom: '10%' }}
    >
      <div
        className="relative w-full"
        style={{ maxWidth: 1084, width: 'min(100%, 1084px, calc((100vh - 120px) * 2.374))', transform: 'translateY(30px) translateX(130px) scale(1.15)', transformOrigin: 'top center' }}
      >
        <img src={mapBackImg} className="w-full" style={{ opacity: 0, pointerEvents: 'none', display: 'block' }} alt="" />
        <img src={mapBackImg} className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ zIndex: 1 }} alt="" />
        <img src={mapDesignImg} className="absolute pointer-events-none select-none" style={{ zIndex: 4, right: 0, top: '50%', transform: 'translate(90px, calc(-50% + 50px))', width: '84%' }} alt="" />

        {/* 스크린 영역 */}
        <div
          ref={screenRef}
          className="absolute overflow-hidden"
          onClick={() => { if (!isDragging) setOpenTownRegionsId(null); }}
          onMouseDown={handleMapMouseDown}
          onMouseMove={handleMapMouseMove}
          onMouseUp={handleMapMouseUp}
          onMouseLeave={handleMapMouseUp}
          style={{ zIndex: 2, left: SCREEN.left, top: SCREEN.top, right: SCREEN.right, bottom: SCREEN.bottom, cursor: isDragging ? 'grabbing' : 'grab' }}
        >

          {/* 항상 깔려있는 검은 배경 */}
          <div style={{ position: 'absolute', inset: 0, background: '#000' }} />

          {/* 레트로 TV 켜짐 효과 — 항상 최상위 */}
          {tvPhase === 0 && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 100, background: '#000', pointerEvents: 'none' }} />
          )}
          {tvPhase === 1 && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 100,
              animation: 'retroTvOn 0.9s ease-out forwards',
              transformOrigin: 'center',
              pointerEvents: 'none',
            }} />
          )}

          {/* 맵 콘텐츠 래퍼 — TV reveal */}
          <div className="absolute inset-0" style={{
            transformOrigin: 'center',
            opacity: tvPhase < 2 ? 0 : 1,
            animation: tvPhase === 2 ? 'retroTvReveal 0.18s ease-out both' : undefined,
          }}>
            {/* 맵 배경 */}
            <div className="absolute inset-0" style={getViewportBgStyle(viewport)} />

          {/* 방향 화살표 */}
          {screenView === 'map' && <>
            <ArrowBtn dir="up" />
            <ArrowBtn dir="down" />
            <ArrowBtn dir="left" />
            <ArrowBtn dir="right" />
          </>}

          {/* 남은 탐험 횟수 — 우측 상단 고정 */}
          <div style={{ position: 'absolute', top: 24, right: 36, zIndex: 55, display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.65))' }}>
            <Footprints size={18} color="#fff" />
            <span style={{ fontFamily: "'Mona12 Text KR','Mona12',monospace", fontSize: 15, fontWeight: 700, color: '#fff' }}>
              {dailyWalks} / {maxDailyWalks}
            </span>
          </div>

          {/* 네비게이션 — 상단 */}
          {/* 마을 핀 */}
          {screenView !== 'detail' && towns.map(town => {
            const coords = toViewportCoords(town.x, town.y, viewport);
            if (coords.x < -8 || coords.x > 108 || coords.y < -8 || coords.y > 108) return null;
            const isHidden = town.groupVisible === false;
            const isActive = town.groupId === selectedTownId;
            const isNewTown = !visitedTownIds.has(town.groupId);
            const townRegions = regions
              .filter(region => region.groupId === town.groupId && !region.isTownMeta && region.groupVisible !== false && region.visible !== false)
              .slice(0, 4);
            const visibleTownRegions = townRegions;
            return (
              <div key={town.groupId} onClick={(event) => {
                event.stopPropagation();
                if (isHidden || isDragging) return;
                playClickSound();
                handlePinClick(town.groupId);
              }}
                role={isHidden ? undefined : 'button'}
                tabIndex={isHidden ? -1 : 0}
                onKeyDown={(event) => {
                  if (isHidden) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.stopPropagation();
                    handlePinClick(town.groupId);
                  }
                }}
                className="absolute flex flex-col items-center gap-0.5 hover:scale-110 transition-transform"
                style={{ left: `${coords.x}%`, top: `${coords.y}%`, transform: 'translate(-50%, -100%)', zIndex: 10, opacity: isActive ? 1 : 0.85, cursor: isHidden ? 'default' : 'pointer', width: 'max-content' }}>
                <div className="relative flex items-center justify-center" style={{ height: 77, width: 156.4 }}>
                  <img src={mapNameImg} alt="" style={{ height: 77, width: 156.4, display: 'block', filter: `${isActive ? `drop-shadow(0 0 3px ${town.color}) ` : ''}drop-shadow(5px 5px 1px rgba(43, 16, 2, 0.55))` }} />
                  <span className="absolute inset-0 flex items-center justify-center whitespace-nowrap" style={{ fontFamily: "'GmarketSans', sans-serif", fontWeight: 700, fontSize: 24, lineHeight: 1, color: '#373a33', imageRendering: 'auto', letterSpacing: 0, fontSynthesis: 'none' }}>
                    <span style={{ position: 'relative', left: -2, top: 3 }}>
                      {town.groupName}
                    </span>
                  </span>
                  {isNewTown && (
                    <span
                      aria-hidden="true"
                      className="absolute rounded-full"
                      style={{
                        top: 19,
                        right: 18,
                        width: 8,
                        height: 8,
                        background: '#ef5e5e',
                      }}
                    />
                  )}
                  {openTownRegionsId === town.groupId && visibleTownRegions.map((region, index) => {
                    const isSide = index === 0 || index === 3;
                    const pillBtn = (
                      <button
                        key={region.id}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          playClickSound();
                          handleSelectArea(region);
                        }}
                        style={{
                          position: isSide ? 'static' : 'absolute',
                          ...(isSide ? {} : regionPillPositions[index]),
                          padding: '8px 16px',
                          boxSizing: 'border-box',
                          border: 'none',
                          borderRadius: 999,
                          background: '#252624',
                          color: '#fff',
                          fontFamily: 'GmarketSans, sans-serif',
                          fontSize: 20,
                          fontWeight: 800,
                          lineHeight: '20px',
                          fontSynthesis: 'none',
                          letterSpacing: 0,
                          whiteSpace: 'normal',
                          textAlign: 'center',
                          cursor: 'pointer',
                          zIndex: 12,
                          animation: `pillPop 0.18s ease-out both`,
                        }}
                      >
                        <span style={{ position: 'relative', top: index === 0 ? 1 : 3, display: 'inline-block', transform: 'scaleX(0.9)', transformOrigin: 'center' }}>
                          {wrapPillText(region.name).map((line, li) => (
                            <span key={li} style={{ display: 'block' }}>{line}</span>
                          ))}
                        </span>
                      </button>
                    );
                    if (isSide) {
                      return (
                        <div
                          key={region.id}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            ...(index === 0 ? { right: -125 } : { left: -125 }),
                            zIndex: 12,
                          }}
                        >
                          {pillBtn}
                        </div>
                      );
                    }
                    return pillBtn;
                  })}
                </div>
              </div>
            );
          })}

          {screenView === 'detail' && <DeviceMobileDetailPanel />}
          </div>{/* 맵 콘텐츠 래퍼 끝 */}

          <img src={mapViewportImg} className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ zIndex: 90 }} alt="" />
        </div>

      </div>
    </div>
  );
}
